import { FC, useRef, useEffect } from "react"

import styled from "styled-components"

import { colors } from "@izettle/otto"

import { LoaderSize } from "../../enumerations"
import { useLottie } from "../../hooks/use-lottie"
import { SpinnerSimple } from "../spinner-simple"

export type LoaderProps = {
  size: LoaderSize
  fullscreen?: boolean
  label?: string
}

const Container = styled.div<Pick<LoaderProps, "fullscreen">>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ fullscreen }) => (fullscreen ? `margin: 200px auto;` : null)}
`

const StyledLottie = styled.div<Pick<LoaderProps, "size">>`
  & * {
    fill: ${colors.spinner.highlight};
    stroke: ${colors.spinner.highlight};
  }

  * {
    fill: ${colors.spinner.highlight};
    stroke: ${colors.spinner.highlight};
  }

  ${({ size }) => {
    switch (size) {
      case LoaderSize.Medium:
        return `
          width: 40px;
          height: 40px;
        `
      case LoaderSize.Large:
      default:
        return `
          width: 80px;
          height: 80px;
        `
    }
  }}
`

/**
 * @param fullscreen false by default
 * @param label optional prop that defaults to `loading spinner`.
 * @param {LoaderSize} size - Requires the LoaderSize enum. Example: ```LoaderSize.Large```.
 * or can be passed in as a string: `size='small'`
 * [More information and Examples can be found here](https://design.izettle.net/components/loader/web)
 */
export const Loader: FC<LoaderProps> = ({
  size,
  fullscreen = false,
  label = "loading spinner",
}) => {
  const isLottiePresent = useLottie()
  const refLottie = useRef(null)
  let animation: any

  useEffect(() => {
    if (!isLottiePresent) return

    if (size !== LoaderSize.Small) {
      // @ts-ignore This component relies on the bodymovin player being present in the DOM.
      animation = window.bodymovin?.loadAnimation({
        container: refLottie.current,
        path: "https://design-cdn.zettle.com/loader/loader.json",
        autoplay: true,
        renderer: "svg",
        loop: true,
      })

      return () => {
        animation?.destroy()
      }
    }
  }, [size, isLottiePresent])

  return process.env.NODE_ENV === "test" || size === LoaderSize.Small ? (
    <Container fullscreen={fullscreen}>
      <SpinnerSimple role="img" aria-label={label} size="xSmall" color="highlight" />
    </Container>
  ) : (
    <Container fullscreen={fullscreen}>
      <StyledLottie role="img" aria-label={label} size={size} ref={refLottie} />
    </Container>
  )
}
